/**
 * Saved places, on this device and nowhere else.
 *
 * A pin was a single slot: saving the tent silently destroyed the car, with no warning and no
 * undo. That is the wrong shape for the feature — the car, the tent and the umbrella are all
 * true at the same time on the same afternoon — so this is a list, newest first, capped so it
 * cannot grow without bound.
 *
 * The privacy rule is unchanged and is the reason this file exists at all: a pin lives in
 * `localStorage` on the device that made it and never reaches the server
 * (`ProductOverview.md` §4.10).
 */

import { normalizeLabel, type Destination } from './destination';
import { isPhotoDataUrl } from './photo';

const KEY = 'beeline:pins';
/** The single-slot key this replaced. Read once, migrated, and removed. */
const LEGACY_KEY = 'beeline:pin';

/**
 * How many places are worth keeping.
 *
 * Past a dozen this stops being "where did I leave the car" and starts being a places database,
 * which is a different product. The oldest falls off the end.
 */
export const MAX_PINS = 12;

export interface SavedPin extends Destination {
  /** Stable across renames and reorders, so a route can name one. */
  id: string;
  label: string;
  pinnedAt: number;
}

const storage = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const isLat = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= -90 && value <= 90;
const isLng = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= -180 && value <= 180;

/** Six decimal places is about 10 cm, which is far beyond anything GPS can tell you. */
const round = (value: number): number => Math.round(value * 1e6) / 1e6;

/**
 * A short, unguessable-enough id.
 *
 * It never leaves the device and never identifies a person, so it only has to be unique within
 * one browser's list. `crypto.randomUUID` is not assumed, because this also runs in tests and in
 * older WebViews.
 */
function newId(): string {
  const random = Math.floor(Math.random() * 0xffffff).toString(36);
  return `${Date.now().toString(36)}${random}`;
}

function sanitize(raw: unknown): SavedPin | null {
  if (!raw || typeof raw !== 'object') return null;
  const entry = raw as Record<string, unknown>;
  if (!isLat(entry['lat']) || !isLng(entry['lng'])) return null;

  const photo = entry['photo'];
  const pinnedAt = typeof entry['pinnedAt'] === 'number' ? entry['pinnedAt'] : Date.now();

  return {
    id: typeof entry['id'] === 'string' && entry['id'] ? entry['id'] : newId(),
    lat: entry['lat'],
    lng: entry['lng'],
    label: normalizeLabel(
      typeof entry['label'] === 'string' ? entry['label'] : null,
      'Pinned spot',
    ),
    pinnedAt: Number.isFinite(pinnedAt) ? pinnedAt : Date.now(),
    accuracy: typeof entry['accuracy'] === 'number' ? entry['accuracy'] : undefined,
    altitude: typeof entry['altitude'] === 'number' ? entry['altitude'] : null,
    photo: isPhotoDataUrl(photo) ? photo : null,
  };
}

/**
 * Move the one pin an older build could hold into the list.
 *
 * Somebody who parked their car under the old version and opened the new one mid-afternoon must
 * still get their car back, so the legacy key is read before it is dropped.
 */
function migrate(store: Storage): SavedPin[] {
  const legacy = store.getItem(LEGACY_KEY);
  if (!legacy) return [];

  store.removeItem(LEGACY_KEY);
  try {
    const pin = sanitize(JSON.parse(legacy));
    if (!pin) return [];
    write(store, [pin]);
    return [pin];
  } catch {
    return [];
  }
}

function read(store: Storage): SavedPin[] {
  const raw = store.getItem(KEY);
  if (!raw) return migrate(store);

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(sanitize).filter((pin): pin is SavedPin => pin !== null);
  } catch {
    return [];
  }
}

function write(store: Storage, pins: SavedPin[]): void {
  const capped = pins.slice(0, MAX_PINS);
  try {
    store.setItem(KEY, JSON.stringify(capped));
  } catch {
    // Almost always the quota, and almost always a photo. Losing the pictures is a far better
    // outcome than losing the places, so try again without them before giving up.
    try {
      store.setItem(KEY, JSON.stringify(capped.map((pin) => ({ ...pin, photo: null }))));
    } catch {
      // A full or disabled store costs the user their places, nothing worse.
    }
  }
}

/** Every saved place, newest first. */
export function listPins(): SavedPin[] {
  const store = storage();
  if (!store) return [];
  return read(store).sort((left, right) => right.pinnedAt - left.pinnedAt);
}

export function getPin(id: string): SavedPin | null {
  return listPins().find((pin) => pin.id === id) ?? null;
}

export const hasPins = (): boolean => listPins().length > 0;

/**
 * Save a place, and hand back what was actually stored.
 *
 * Returns the record because the caller almost always needs its id straight away — to navigate
 * to it, or to show "saved" against the right row.
 */
export function savePin(destination: Destination): SavedPin {
  const pin: SavedPin = {
    id: newId(),
    lat: round(destination.lat),
    lng: round(destination.lng),
    label: normalizeLabel(destination.label, 'Pinned spot'),
    pinnedAt: destination.pinnedAt ?? Date.now(),
    accuracy: destination.accuracy,
    altitude: destination.altitude ?? null,
    photo: isPhotoDataUrl(destination.photo) ? destination.photo : null,
  };

  const store = storage();
  if (!store) return pin;

  write(store, [pin, ...read(store)]);
  return pin;
}

export function removePin(id: string): void {
  const store = storage();
  if (!store) return;
  write(
    store,
    read(store).filter((pin) => pin.id !== id),
  );
}

/** Used by the tests and by "forget everything" paths. */
export function clearPins(): void {
  const store = storage();
  if (!store) return;
  store.removeItem(KEY);
  store.removeItem(LEGACY_KEY);
}
